// Open Studio: this module no longer fetches or mutates a node graph. It builds
// the semantic params payload (plan section 3) the handler assembles into a
// pipeline. No node IDs, no graph fetch, no graph send.
import { createLogger } from '../utils/logger.js';
import { getSession } from '../session.js';
import * as store from './generation-store.js';

const logDebug = createLogger('Workflow');

// Scalar params that ride in the payload even when no {param} tag drives them
// (plan section 3 schema). cfg/aspect have no workflow tag today; tag-backed
// keys (seed, steps, denoise, frame_divider, ...) are overlaid from controls.
const SCALAR_DEFAULTS = {
  method: 'forge',
  prompt: '', negative_prompt: '',
  seed: -1, steps: 20, cfg: 6.0, denoise: 0.7,
  aspect: '16:9', frame_divider: 2,
};

// Mint the canonical generation id (plan 10.1): gen_<timestamp>_<random>.
export function mintGenerationId() {
  const rand = Math.random().toString(36).slice(2, 10);
  return `gen_${Date.now()}_${rand}`;
}

// Build the request payload from the controls store + uploaded file slots.
// Returns { generation_id, user_id, params:{...} } (plan section 3).
export function buildParams(generationId) {
  const generation_id = generationId || mintGenerationId();
  const user_id = getSession()?.user?.userId || null;

  const method = store.getMethod();
  const controlValues = store.getParams();
  const features = store.getFeatures();
  const files = store.getFiles();
  const uiOnly = store.getUiOnlyParams();

  // uiOnly params are seeded into the store so showWhen children can key on
  // them, but they must not ride in the request payload (they have no workflow
  // {param} tag and would surface as orphans / unknown keys to the handler).
  const filtered = {};
  for (const [k, v] of Object.entries(controlValues)) {
    if (uiOnly.has(k)) continue;
    filtered[k] = v;
  }

  const params = { ...SCALAR_DEFAULTS, ...filtered, method, features, files };

  // Envision VFX (vfx-gallery.js): the trigger phrase is prepended client-side
  // for the no-auto-improve path; it also rides in the payload as vfx_trigger
  // so the worker can re-prepend it after prompt_prep rewrites the prompt.
  const vfxTrigger = controlValues.vfx_trigger;
  if (vfxTrigger) {
    params.prompt = `${vfxTrigger} ${params.prompt || ''}`.trim();
    logDebug('Prepended VFX trigger to prompt', { vfxTrigger });
  }

  logDebug('Built params payload', { generation_id, method,
    paramKeys: Object.keys(filtered), features, fileSlots: Object.keys(files),
    uiOnly: [...uiOnly] });

  return { generation_id, user_id, params };
}
